import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { ProductInterface, TProductFilter } from "../../interfaces/productInterface";
import { useCategoriesQuery } from "../../features/categories/queries/useCategoriesQuery";
import { OptionType } from "../../components/common/Inputs/SelectInput";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import {
  faCheck,
  faEdit,
  faFileInvoiceDollar,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  productSchema,
  useProductsFilter,
  useProduct,
  useSaveProduct,
  useProductStatusToggle,
  ProductForm,
  ProductTable,
  ProductsHeader,
} from "../../features/products";

const formDataInit: ProductInterface = {
  name: "",
  categoryId: "",
  price: null,
  cost: null,
  isBulk: false,
  active: true,
};

const filterInit: TProductFilter = {};

const ProductPage = () => {
  const navigate = useNavigate();
  const { data: categoriesResponse } = useCategoriesQuery({ enabled: true });
  const categories = useMemo(
    () => categoriesResponse?.data ?? [],
    [categoriesResponse],
  );
  const [categoriesOptions, setCategoriesOptions] = useState<OptionType[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "createdAt",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TProductFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { data: products, paginationData, isLoading } = useProductsFilter({
    filter,
    pagination,
  });

  const { data: productToEdit } = useProduct({
    productId: editingProductId ?? undefined,
  });

  const { handleSave } = useSaveProduct();
  const { handleEnable, handleDisable } = useProductStatusToggle();

  useEffect(() => {
    const nOptions: OptionType[] = categories.map(({ _id, name }) => ({
      value: _id ? _id : "",
      label: name,
    }));
    setCategoriesOptions(nOptions);
  }, [categories]);

  const categoryNameById = useMemo(() => {
    return categories.reduce<Record<string, string>>((acc, category) => {
      if (category._id) acc[category._id] = category.name;
      return acc;
    }, {});
  }, [categories]);

  const formik = useFormik<ProductInterface>({
    initialValues: productToEdit || formDataInit,
    validationSchema: productSchema,
    enableReinitialize: true,
    onSubmit: (
      values: ProductInterface,
      formikHelpers: FormikHelpers<ProductInterface>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewProductModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingProductId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingProductId(null);
    setShowModal(false);
  };

  const handleEditProduct = (product: ProductInterface) => {
    const { _id: productId } = product;
    if (!productId) return;
    setEditingProductId(productId);
    setShowModal(true);
  };

  const handleNavigateToPriceHistory = (product: ProductInterface) => {
    navigate(`price-history/${product._id}`);
  };

  return (
    <div>
      <ProductsHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewProductModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <ProductTable
        data={products || []}
        isLoading={isLoading}
        categoryNameById={categoryNameById}
        sortBy={pagination.sortBy as keyof ProductInterface}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditProduct,
          },
          {
            title: "Historial de Precios",
            actionKey: "product-price-history",
            icon: faFileInvoiceDollar,
            onClick: handleNavigateToPriceHistory,
            color: "success",
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <ProductForm formik={formik} categoriesOptions={categoriesOptions} />
      </Modal>
    </div>
  );
};

export default ProductPage;
